import { Form, FormikProvider, useFormik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tournamentsTeamsService from "../../services/tournamentsTeams";
import { IMAGE_BASE } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import {
  Avatar,
  Box,
  Button,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import CardLayout from "../../layouts/CardLayout";
import { LoadingButton } from "@mui/lab";
import FieldErrorMessage from "../../components/FieldErrorMessage";

const UpdateTeam = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchTeamsdata = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await tournamentsTeamsService.getOneTeam({ id });

        const team = await response?.data?.data;

        setData(team);

        if (team?.logo_url) {
          const fullImageUrl = IMAGE_BASE + team?.logo_url;

          setImagePreview(fullImageUrl);
        }
      } catch (error) {
        if (data?.id !== id) {
          navigate("/tournaments", { replace: true });
        }
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    } else {
      setData(null);
    }
  }, [id]);

  useEffect(() => {
    fetchTeamsdata();
  }, [fetchTeamsdata]);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const response = await fetch(`${IMAGE_BASE}${data?.logo_url}`);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setImagePreview(url);
      } catch (err) {
        console.error("Failed to load image", err);
      }
    };

    if (data?.logo_url) {
      loadImage();
    }
  }, [data]);

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      logo_url: data?.logo_url || "",
      country:data?.country || ""
    },

    onSubmit: async (values) => {
      setLoading(true);

      try {
        const formData = new FormData();

        formData.append("image", values.logo_url);
        formData.append("country",values.country)
        formData.append("imagePath", "team");

        if (id) {
          formData.append("id", id);
        }

        if (selectedImage && selectedImage !== data?.logo_url) {
          formData.append("image", selectedImage);
        }

        const response = tournamentsTeamsService.updateTeam(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (selectedImage && selectedImage.name === file?.name) {
      toast.error("You cannot upload the same image again.");
      fileInputRef.current.value = "";
      return;
    }

    if (file && validTypes.includes(file.type)) {
      setImageLoading(true);
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));

      formik.setFieldValue("image", file);
      setTimeout(() => {
        setImageLoading(false);
      }, 2000);
    } else {
      toast.error("Please upload a valid image (JPEG, PNG, WEBOP or GIF).");
      fileInputRef.current.value = "";
    }
  };

  const {
    values,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    resetForm,
    handleChange,
  } = formik;

  return (
    <div>
      <Box>
        <Helmet>
          <title>Edit Team</title>
        </Helmet>
        <CardLayout>
          <CardHeader title={`Edit Team`} />
          <CardContent>
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" mb={1} align="center">
                      Upload Team Image
                    </Typography>
                    <input
                      accept="image/*"
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                    <Box
                      mt={1}
                      onClick={() => fileInputRef.current.click()}
                      className="upload-box"
                    >
                      {imageLoading ? (
                        <CircularProgress
                          style={{
                            display: "block",
                            margin: "0 auto",
                            cursor: "pointer",
                          }}
                          size={24}
                        />
                      ) : (
                        <Avatar
                          style={{
                            display: "block",
                            margin: "0 auto",
                            cursor: "pointer",
                          }}
                          src={imagePreview}
                          alt="Team Image"
                          className="avatar"
                        />
                      )}
                    </Box>
                    {formik.touched.image && formik.errors.image && (
                      <div
                        style={{
                          color: "#FF4842",
                          fontSize: "0.75rem",
                          textAlign: "center",
                        }}
                      >
                        {formik.errors.image}
                      </div>
                    )}
                    <Box mt={2} display="flex" justifyContent="center">
                      <Button
                        size="large"
                        type="button"
                        variant="contained"
                        onClick={() => fileInputRef.current.click()}
                      >
                        Upload Team Image
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Country"
                      required
                      type="text"
                      error={Boolean(touched?.country && errors?.country)}
                      {...getFieldProps("country")}
                      onKeyDown={(e) => {
                        if ((e.key >= '0' && e.key <= '9') || ["e", "E", "+", "-"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <FieldErrorMessage formik={formik} name="country" />
                  </Grid>

                  <Grid item xs={12}>
                    <Box mt={2}>
                      <LoadingButton
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        loading={loading}
                      >
                        Update Team
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </CardLayout>
      </Box>
    </div>
  );
};

export default UpdateTeam;
